import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Users, Star, Mail, Phone } from 'lucide-react';
import api from '../api';
import { Contact, PaginatedResult } from '../types';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';

const ContactListPage: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [decisionMakerFilter, setDecisionMakerFilter] = useState('');
  const [companyFilter] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', isDecisionMaker: false,
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { page, limit };
      if (search) params.search = search;
      if (decisionMakerFilter !== '') params.isDecisionMaker = decisionMakerFilter;
      if (companyFilter) params.companyId = companyFilter;

      const res = await api.get<PaginatedResult<Contact>>('/contacts', { params });
      setContacts(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError('Erreur lors du chargement des contacts.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, decisionMakerFilter, companyFilter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchContacts();
  };

  const handleAddContact = async () => {
    setFormError('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError('Le prénom et le nom sont obligatoires.');
      return;
    }
    setFormLoading(true);
    try {
      const res = await api.post('/contacts', form);
      setShowAddModal(false);
      setForm({ firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', isDecisionMaker: false });
      navigate(`/contacts/${res.data.id}`);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setFormLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h1>Contacts</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={16} /> Ajouter
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="toolbar">
            <div className="toolbar-left">
              <form onSubmit={handleSearch} className="search-bar">
                <Search />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>
            </div>
          </div>

          <div className="filter-bar">
            <select
              className="form-select"
              value={decisionMakerFilter}
              onChange={(e) => { setDecisionMakerFilter(e.target.value); setPage(1); }}
            >
              <option value="">Tous les contacts</option>
              <option value="true">Décideurs uniquement</option>
              <option value="false">Non décideurs</option>
            </select>
          </div>

          {loading ? (
            <div className="spinner-container"><div className="spinner" /></div>
          ) : error ? (
            <div className="empty-state"><p className="empty-state-title">{error}</p></div>
          ) : contacts.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <p className="empty-state-title">Aucun contact trouvé</p>
              <p className="empty-state-description">Modifiez vos filtres ou ajoutez un contact.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table table-clickable">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Poste</th>
                    <th>Entreprise</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Décideur</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)}>
                      <td className="font-semibold">{c.firstName} {c.lastName}</td>
                      <td>{c.jobTitle || '-'}</td>
                      <td>
                        {c.company ? (
                          <span
                            className="text-primary cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); navigate(`/companies/${c.company!.id}`); }}
                          >
                            {c.company.name}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        {c.email ? <span className="inline-flex"><Mail size={14} /> {c.email}</span> : '-'}
                      </td>
                      <td>
                        {(c.phone || c.mobile) ? <span className="inline-flex"><Phone size={14} /> {c.phone || c.mobile}</span> : '-'}
                      </td>
                      <td>
                        {c.isDecisionMaker && <span className="badge badge-purple"><Star size={12} /> Décideur</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} total={total} />
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Ajouter un contact"
        footer={
          <>
            <button className="btn" onClick={() => setShowAddModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddContact} disabled={formLoading}>
              {formLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {formError && <div className="login-error">{formError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Prénom *</label>
            <input className="form-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="form-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Téléphone</label>
            <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile</label>
            <input className="form-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Poste</label>
          <input className="form-input" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-check">
            <input type="checkbox" checked={form.isDecisionMaker} onChange={(e) => setForm({ ...form, isDecisionMaker: e.target.checked })} />
            Décideur
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default ContactListPage;
